import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'space-shield-theme';

/** @typedef {'system' | 'light' | 'dark'} ThemeMode */

const ThemeContext = createContext(null);

function readStoredMode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    /* ignore */
  }
  return 'system';
}

function writeStoredMode(mode) {
  try {
    if (mode === 'system') {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, mode);
    }
  } catch {
    /* ignore */
  }
}

function getSystemDark() {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function ThemeProvider({ children }) {
  const [themeMode, setThemeModeState] = useState(() => readStoredMode());
  const [systemDark, setSystemDark] = useState(() =>
    typeof window !== 'undefined' ? getSystemDark() : false,
  );

  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    writeStoredMode(mode);
  }, []);

  const setToSystem = useCallback(() => {
    setThemeModeState('system');
    writeStoredMode('system');
  }, []);

  useEffect(() => {
    if (themeMode !== 'system') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystemDark(mq.matches);
    mq.addEventListener('change', onChange);
    setSystemDark(mq.matches);
    return () => mq.removeEventListener('change', onChange);
  }, [themeMode]);

  const resolvedTheme = useMemo(() => {
    if (themeMode === 'system') {
      return systemDark ? 'dark' : 'light';
    }
    return themeMode;
  }, [themeMode, systemDark]);

  useLayoutEffect(() => {
    document.documentElement.classList.toggle('dark', resolvedTheme === 'dark');
  }, [resolvedTheme]);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    meta.setAttribute(
      'content',
      resolvedTheme === 'dark' ? '#000000' : '#f9fafb',
    );
  }, [resolvedTheme]);

  const value = useMemo(
    () => ({
      themeMode,
      resolvedTheme,
      setThemeMode,
      setToSystem,
    }),
    [themeMode, resolvedTheme, setThemeMode, setToSystem],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}

/** Recharts SVG props — Tailwind does not apply inside chart SVG. */
export function useChartTheme() {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return useMemo(
    () => ({
      grid: isDark ? 'rgba(255,255,255,0.06)' : '#e5e7eb',
      axisStroke: isDark ? 'rgba(255,255,255,0.12)' : '#9ca3af',
      tickFill: isDark ? '#71717a' : '#4b5563',
      tooltipBg: isDark ? '#0a0a0a' : '#ffffff',
      tooltipBorder: isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
      tooltipLabel: isDark ? '#e4e4e7' : '#111827',
      tooltipItem: isDark ? '#a1a1aa' : '#374151',
      legend: isDark ? '#71717a' : '#6b7280',
    }),
    [isDark],
  );
}
